/** Pipeline card component. Wraps card content with @dnd-kit sortable functionality. */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

import { PIPELINE_PANEL_CLASSES } from './PipelinePanel';

/**
 * Brand-violet flash applied to a card that has just been re-sorted after a
 * cross-stage drop. Defined in index.css.
 *
 * Not `focus-highlight` (the generic deep-link affordance): that class forces
 * `border-radius: var(--radius-xl)`, which would distort this card's
 * `rounded-[5px]` corners for the length of the animation.
 */
const RESORT_FLASH_CLASS = 'pipeline-resort-flash';

/**
 * Persistent brand-violet highlight for the card whose record is open in the
 * slideout. Same visual language as the re-sort flash above, held steady rather
 * than animated. Defined in index.css.
 */
const OPEN_IN_SLIDEOUT_CLASS = 'pipeline-open-in-slideout';

/** Card container styles — extends the shared panel with card bg and dark mode elevation */
const CARD_CLASSES = `${PIPELINE_PANEL_CLASSES} bg-bg-primary dark:bg-bg-tertiary pt-3 pb-1 px-0`;

interface PipelineCardProps {
  /** Unique ID for sorting */
  id: string | number;
  /** Content to render inside the card */
  children: React.ReactNode;
  /** Called when the card is clicked (not dragged) */
  onClick?: () => void;
  /** Whether this card is a placeholder (original position while dragging) */
  isPlaceholder?: boolean;
  /**
   * Briefly flashes the card so the eye can reacquire it after it has been
   * re-sorted into a new position following a cross-stage drop.
   *
   * State-driven on purpose: the board owns the "which card just moved" fact
   * and passes it down, rather than a post-render `classList.add` — React owns
   * this element's className (see docs/claude/FRONTEND_PATTERNS.md § Never
   * Manipulate the DOM Directly).
   */
  isHighlighted?: boolean;
  /**
   * Sentence-case sort name (e.g. "date created"). When set, renders a
   * transient "Resorting by …" badge floating OVER this card, explaining why it
   * is about to move — and then why it moved.
   *
   * Overlaid rather than placed in the column's flow for two reasons: it can't
   * shift the card list when it appears/disappears (the in-flow version pushed
   * every card down, then back up), and anchoring it to the card puts the label
   * exactly where the user is already looking.
   */
  resortNotice?: string | null;
  /**
   * Whether the notice is fading out. Kept mounted while true so
   * `animate-out fade-out` can play — the project idiom for exit animations
   * (see lib/base/tooltip/tooltip.tsx:487).
   */
  isResortNoticeExiting?: boolean;
  /**
   * Whether this card's record is the one currently open in the slideout.
   *
   * Renders a persistent brand wash + outline — deliberately a different
   * affordance from the transient `isHighlighted` flash: that one says "this
   * just moved" and fades, this one says "this is what the panel is showing"
   * and stays for as long as the panel does, including while the card is being
   * dragged (the highlight rides along on the placeholder).
   */
  isOpenInSlideout?: boolean;
}

/**
 * Card wrapper — entire card is draggable, white background.
 */
export function PipelineCard({
  id,
  children,
  onClick,
  isPlaceholder = false,
  isHighlighted = false,
  resortNotice,
  isResortNoticeExiting = false,
  isOpenInSlideout = false,
}: PipelineCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(id),
    // dnd-kit's DEFAULT animateLayoutChanges — do not override to () => true.
    // Always-animate meant every regroup (a WS update, another move's server
    // confirmation, a safety-timeout snap-back) transform-tweened cards the
    // user wasn't touching, which mid-drag reads as a second card dragging
    // itself. The default (`defaultAnimateLayoutChanges`) gates on
    // `wasDragging`: siblings still slide aside during an active drag, but
    // layout changes with no drag involved — exactly the WS-driven regroups —
    // apply instantly with no tween. The columns' useSortable learned the
    // same lesson (see the "boomerang" note in PipelineColumn.tsx).
  });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition]
  );

  // Handle card click (but not when dragging)
  const handleClick = () => {
    if (isDragging) {
      return;
    }
    onClick?.();
  };

  // When this card is being dragged, show a placeholder. Keep the
  // open-in-slideout highlight on it: the slideout stays open while its card is
  // dragged, so the placeholder holding the highlight preserves the "which card
  // is the panel showing" context for as long as the panel does (the drag
  // overlay renders raw card content, not this component, so the class can't
  // live there). The `opacity-40` ghost treatment naturally softens it.
  if (isDragging || isPlaceholder) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        data-row-id={id}
        className={cx(
          'group relative',
          CARD_CLASSES,
          'border-2 border-dashed opacity-40',
          isOpenInSlideout && OPEN_IN_SLIDEOUT_CLASS
        )}
        data-state={isOpenInSlideout ? 'open' : undefined}
        aria-hidden='true'
      >
        <div className='invisible'>{children}</div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      data-row-id={id}
      className={cx(
        'group relative',
        CARD_CLASSES,
        'cursor-grab active:cursor-grabbing transition-all duration-150 hover:shadow-md touch-manipulation',
        isHighlighted && RESORT_FLASH_CLASS,
        isOpenInSlideout && OPEN_IN_SLIDEOUT_CLASS
      )}
      data-state={isOpenInSlideout ? 'open' : undefined}
      // Root click/keydown handlers stay here (in addition to the overlay
      // button below) because jsdom does not hit-test: a `fireEvent.click`
      // dispatched at this root in the board test suites would never reach
      // an absolutely-positioned overlay child. Real pointer/keyboard users
      // land on the overlay button; jsdom root-click tests land here. Both
      // paths call the same `handleClick`.
      onClick={handleClick}
      onKeyDown={e => {
        listeners?.onKeyDown?.(e);
        if (e.defaultPrevented) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Accessible open action. Under the ARIA presentational-children rule,
          descendants of an element with a button role (which this root used to
          carry) are not exposed to assistive tech, so interactive descendants
          added by sibling wiring (owner picker, title editor) would be
          invisible to AT. This inset overlay button takes over the button role
          instead, leaving the root a plain, non-presentational container.
          Pattern from HighlightCardShell.tsx.

          `pointer-events-none` is load-bearing, not cosmetic. This overlay is
          an ASSISTIVE-TECH affordance only — pointer users are served by the
          root's own `onClick` above. Left hit-testable, it covers the whole
          card (`inset-0 h-full w-full`) and swallows every press: `Button` is
          react-aria-components -> useButton -> usePress, whose
          `onMouseDown` calls `e.stopPropagation()` for button 0. dnd-kit's
          MouseSensor activator lives on the ROOT (via `useSortable`
          listeners), so a stopped mousedown means no drag EVER starts — which
          is exactly what shipped in #11933 and killed pipeline drag-and-drop.
          Keyboard focus and Enter/Space activation are unaffected by
          `pointer-events` (it only governs pointer hit-testing), so the a11y
          contract this button exists for is fully preserved. Regression test:
          PipelineCard.dragActivation.test.tsx. */}
      <Button
        type='button'
        color='tertiary'
        onClick={handleClick}
        aria-label='Open record'
        className='pointer-events-none absolute inset-0 z-0 h-full w-full rounded-[5px] hover:bg-transparent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
      />

      <div className='pointer-events-none relative'>
        {children}

        {/* Floating "Resorting by …" notice.
            - `absolute` + `pointer-events-none`: costs no layout space, so
              appearing/disappearing never reflows the column, and it can't
              swallow clicks or interfere with a subsequent drag.
            - `aria-hidden`: dnd-kit's live-region announcements already narrate
              the move (PipelineBoard `announcements`). Exposing this too would
              double-announce AND append its text to the card's accessible name,
              which the e2e drag test matches on. */}
        {resortNotice ? (
          <div
            className={cx(
              'pointer-events-none absolute right-2 top-2 z-10',
              // tailwindcss-animate (enabled in index.css). Fades in on arrival
              // and out on departure instead of popping in both directions.
              isResortNoticeExiting
                ? 'animate-out fade-out zoom-out-95 duration-[320ms] ease-in fill-mode-forwards'
                : 'animate-in fade-in zoom-in-95 duration-200 ease-out'
            )}
            aria-hidden='true'
            data-component='pipeline-resorting-badge'
            data-instance={String(id)}
          >
            <Badge color='brand' size='sm' className='shadow-md'>
              {`Resorting by ${resortNotice}`}
            </Badge>
          </div>
        ) : null}
      </div>
    </div>
  );
}
